/**
 * Home page — static data constants extracted for legibility.
 */
import { Salad, Recycle, Hammer, Shirt, SunMedium, Wheat, BatteryCharging, Cpu, CircleDot, Factory, HandHeart, Network as NetIcon } from "lucide-react";

export const HERO_BG =
  "https://images.unsplash.com/photo-1670689334799-cdc6777db8cc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1900";

export const TICKER_ITEMS = [
  { icon: Salad,           from: "Food waste",     to: "Insect protein + fertilizer", tag: "$220/t" },
  { icon: Recycle,         from: "Plastic waste",  to: "Food-grade rPET resin",       tag: "$650/t" },
  { icon: Hammer,          from: "C&D rubble",     to: "Aggregate + M-sand",          tag: "$35/t"  },
  { icon: Shirt,           from: "Textile offcuts",to: "Recycled cotton yarn",        tag: "$420/t" },
  { icon: SunMedium,       from: "Solar panels",   to: "Glass · Aluminium · Silver",  tag: "$720/t" },
  { icon: Wheat,           from: "Agri-residue",   to: "Biochar + biogas",            tag: "$160/t" },
  { icon: BatteryCharging, from: "Used batteries", to: "Black mass + lithium",        tag: "$3.2k/t"},
  { icon: Cpu,             from: "E-waste",        to: "Au · Pd · Cu recovery",       tag: "$4.5k/t"},
];

export const LAYERS = [
  { tag: "01 · Nodes",      icon: CircleDot, title: "Resource-generating Nodes",   body: "Farmers, cities, factories, landowners, recyclers, communities — every source of waste becomes a verified node on the network." },
  { tag: "02 · Hubs",       icon: Factory,   title: "Revenue-producing Hubs",      body: "Modular circular infrastructure converts resources into products: pellets, protein, fibre, aggregates, metals, biochar, energy." },
  { tag: "03 · Network",    icon: NetIcon,   title: "Verification + Finance + Markets", body: "Matching, traceability, financing, offtake, digital product passports and credits — coordinated as one investable system." },
  { tag: "04 · Foundation", icon: HandHeart, title: "People & Communities",        body: "Income share, microloans, insurance, scholarships and skills — circular growth that compounds for those building it." },
];

export const PEOPLE_TEASER_IMAGES = [
  {
    src:   "https://customer-assets.emergentagent.com/job_circular-os/artifacts/qsbpipvs_image.png",
    hover: "https://customer-assets.emergentagent.com/job_circular-os/artifacts/oq5vx5ab_image.png",
    alt:   "Portrait of a community recycler — a person in the Circularity network",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_circular-os/artifacts/9vkm8f4t_image.png",
    hover: "https://customer-assets.emergentagent.com/job_circular-os/artifacts/2eyudcu3_image.png",
    alt:   "Portrait of a smallholder farmer — at the source of the Circularity network",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_circular-os/artifacts/6rhdpyki_image.png",
    hover: "https://customer-assets.emergentagent.com/job_circular-os/artifacts/4on1b9ws_image.png",
    alt:   "Portrait of a Hub collection-truck operator — running the infrastructure",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_circular-os/artifacts/89ji3zk1_image.png",
    hover: "https://customer-assets.emergentagent.com/job_circular-os/artifacts/u37furtx_image.png",
    alt:   "Portrait of a village elder — the matriarch carrying the community forward",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_circular-os/artifacts/o7zx8ota_image.png",
    hover: "https://customer-assets.emergentagent.com/job_circular-os/artifacts/7f53hm6h_image.png",
    alt:   "Portrait of a young woman worker — the next generation of the Circularity network",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_circular-os/artifacts/fx0l07ki_image.png",
    hover: "https://customer-assets.emergentagent.com/job_circular-os/artifacts/yf936rey_image.png",
    alt:   "Portrait of a young construction worker — the next generation building the Circularity network",
  },
];

export const STAT_BAND = [
  { stat: "9",     labelKey: "hubs" },
  { stat: "120+",  labelKey: "byproducts" },
  { stat: "18k+",  labelKey: "lives" },
  { stat: "30s",   labelKey: "time" },
];

export const FOR_PERSONAS = [
  { key: "governments",     label: "Governments",       to: "/for/governments" },
  { key: "landowners",      label: "Landowners",        to: "/for/landowners" },
  { key: "waste",           label: "Waste sources",     to: "/for/waste-sources" },
  { key: "recycling",       label: "Recycling tech",    to: "/for/recycling-tech" },
  { key: "software",        label: "Software partners", to: "/for/software-partners" },
  { key: "offtake",         label: "Offtake buyers",    to: "/for/offtake-buyers" },
  { key: "investors",       label: "Investors",         to: "/for/investors" },
  { key: "fintech",         label: "Fintech / Banks",   to: "/for/fintech" },
  { key: "corporates",      label: "Corporates",        to: "/for/corporates" },
];

export const NETWORK_PROOF_CARDS = [
  { id: "maccia-gtu-100-villages",      t: "Maharashtra · MACCIA + GTU", b: "100 Village Circular Economy Pilot", s: "mou_signed" },
  { id: "axis-bank-shg-pilot",          t: "India · Axis Bank",          b: "SHG & education pilot pathway for circular micro-enterprises", s: "exploratory" },
  { id: "green-worms-kerala",           t: "Kerala · Green Worms",       b: "Plastic recovery network — verified Node reference", s: "under_discussion" },
  { id: "circularity-foundation-india", t: "India · Foundation",          b: "Section 8 incorporation roadmap", s: "proposed" },
  { id: "tadweer",                      t: "UAE · Tadweer",              b: "Cross-border circular partnership exploration", s: "exploratory" },
  { id: "indusind-bank-pilot",          t: "India · IndusInd Bank",      b: "Circular project finance pilot pathway", s: "exploratory" },
];

export const NP_STATUS_TONE = {
  mou_signed: "text-signal-lime", confirmed: "text-signal-lime", active: "text-signal-lime",
  pilot: "text-signal-cyan", announced: "text-signal-cyan", completed: "text-signal-cyan",
};
